/**
 * Existing-table mapping (S6.T2b) action bodies + selector, extracted from
 * `dataImportStore.ts` to keep that file at state-shape + wiring (≤200 lines
 * per CLAUDE.md), matching `dataImportStore.columnActions.ts`'s split.
 *
 * `upsertMappingWithSteal`/`removeMappingEntry` are the pure array transforms
 * — exported (not just used internally) so `useDataImportConfigActions`'s
 * write-both mirror can compute the SAME resultant `mappings` array to write
 * into `content` that the runtime store action independently derives, with no
 * second copy of the atomic-steal logic to drift out of sync (S6.T2a's "one
 * gate, no second list" principle applied to this state layer).
 *
 * ATOMIC STEAL invariant (plan §2, this task's brief): a file `sourceIndex`
 * maps to AT MOST ONE target. Assigning it to a new target silently drops any
 * OTHER target's entry that referenced the same source, in the SAME update —
 * both the grid-header and rail-popover mapping UI (S6.T2c) dispatch
 * `setMapping`, so enforcing this once here means neither surface can drift
 * from the other or produce a two-targets-one-source state.
 */
import type { ImportTargetMapping } from '../../../../types/electron';
import type { DataImportEntry } from './dataImportStore';

/** Stable empty-array reference (module-level) — see `ColumnsFilterPanel.tsx`'s
 *  identical comment: a fresh `?? []` on every call would defeat `mappingByTarget`'s
 *  cache below (a new array is never `===` the previous one). */
const EMPTY_MAPPINGS: ImportTargetMapping[] = [];

/**
 * Upsert `mapping` by `targetColumn` (replaces any existing entry for that
 * target), then — ONLY when `mapping.source.kind === 'file'` — drops any
 * OTHER target's entry that references the same `sourceIndex`. A
 * `kind: 'fixed'` mapping has no source column, so it never steals.
 */
export function upsertMappingWithSteal(
  mappings: ImportTargetMapping[],
  mapping: ImportTargetMapping,
): ImportTargetMapping[] {
  const stolenSourceIndex = mapping.source.kind === 'file' ? mapping.source.sourceIndex : undefined;
  const kept = mappings.filter((m) => {
    if (m.targetColumn === mapping.targetColumn) return false; // upsert: this target's own prior entry is replaced
    if (stolenSourceIndex !== undefined && m.source.kind === 'file' && m.source.sourceIndex === stolenSourceIndex) {
      return false; // atomic steal: no other target may keep the same file source
    }
    return true;
  });
  return [...kept, mapping];
}

/** Removes the entry for `targetColumn`; a no-op (same array-shape result) if absent. */
export function removeMappingEntry(mappings: ImportTargetMapping[], targetColumn: string): ImportTargetMapping[] {
  return mappings.filter((m) => m.targetColumn !== targetColumn);
}

/** `setMapping`'s body. */
export function setMappingAction(entry: DataImportEntry, mapping: ImportTargetMapping): DataImportEntry {
  return { ...entry, mappings: upsertMappingWithSteal(entry.mappings ?? EMPTY_MAPPINGS, mapping) };
}

/** `clearMapping`'s body. */
export function clearMappingAction(entry: DataImportEntry, targetColumn: string): DataImportEntry {
  return { ...entry, mappings: removeMappingEntry(entry.mappings ?? EMPTY_MAPPINGS, targetColumn) };
}

/** `setMappings`'s body — wholesale replace (auto-map suggestions apply, destination-change reset). */
export function setMappingsAction(entry: DataImportEntry, mappings: ImportTargetMapping[]): DataImportEntry {
  return { ...entry, mappings };
}

// Keyed by the `mappings` ARRAY reference (not tabId/entry) so two entries that
// happen to share the same (e.g. both-empty) array still hit the cache, and so
// the cache never needs manual invalidation — a mutated entry always gets a
// fresh `mappings` array reference from the actions above, which naturally
// misses the cache and recomputes once.
const mappingByTargetCache = new WeakMap<ImportTargetMapping[], Map<string, ImportTargetMapping>>();

/**
 * O(1)-lookup view of an entry's mappings, keyed by `targetColumn`. Returns
 * the SAME `Map` instance across calls as long as `entry.mappings` is the
 * same array reference — required so a component reading this through
 * `useShallow` (or a plain `===` memo) doesn't re-render every time purely
 * because this selector handed back a structurally-equal-but-new `Map`
 * (the React #185 trap documented throughout this feature, e.g.
 * `ColumnsFilterPanel.tsx`'s `EMPTY_COLUMNS`).
 */
export function mappingByTarget(entry: Pick<DataImportEntry, 'mappings'>): Map<string, ImportTargetMapping> {
  const mappings = entry.mappings ?? EMPTY_MAPPINGS;
  const cached = mappingByTargetCache.get(mappings);
  if (cached) return cached;
  const map = new Map(mappings.map((m) => [m.targetColumn, m]));
  mappingByTargetCache.set(mappings, map);
  return map;
}
